import { getVariantPrice } from "@babybjorn/utils/commercetools/money";
import type { TypedMoney } from "@commercetools/platform-sdk";
import type { TCountryCode } from "countries-list";
import type { Attribute } from "../../type-definitions/gatsby-type-union";
import { formatMoney } from "../cart/currency";
import {
	getSlugForProductVariant,
	getVariantImages,
	getVariantStringAttribute,
	type Image,
} from "../storyblok/product-variant";
import {
	isProductStringAttribute,
	isValidStringAttribute,
} from "../type-guards/product-attribute";
import { getLocalSku } from "./get-local-sku";

interface Variant {
	id: string;
	color?: string | null;
	fabric?: string | null;
	material?: string | null;
	variantUrl: string;
	sort_order: number;
	images: Image[];
	price: string;
	rawPrice: TypedMoney;
	sku?: string | null;
	colorHex1: string;
	colorHex2?: string | null;
}

export const getVariantAttr = (
	variant: Queries.Maybe<Queries.ProductVariantFragment>,
	name: string,
): Attribute | undefined =>
	variant?.attributes.find((attr) => attr.name === name);

export const getAttributeStringValue = (attr: unknown): string | undefined =>
	isProductStringAttribute(attr) && isValidStringAttribute(attr)
		? attr.stringValue
		: undefined;

export const getHexOneForVariant = (
	variant?: Queries.ProductVariantFragment | null,
): string =>
	(variant && getVariantStringAttribute(variant, "hex-color-patch-1")) ??
	"777777";

export const getColorForVariant = (
	variant?: Queries.ProductVariantFragment | null,
): string =>
	(variant && getVariantStringAttribute(variant, "color")) ?? "Anthracite";

export const mapProductVariant = (
	variant: Queries.ProductVariantFragment,
	currency: string,
	country?: TCountryCode,
): Variant | undefined => {
	const colorAttr = getVariantAttr(variant, "color");
	const fabricAttr = getVariantAttr(variant, "fabric");
	const materialsAttr = getVariantAttr(variant, "materials");
	const slug = getSlugForProductVariant(variant);
	const images = getVariantImages(variant) ?? [];
	const colorHex1 = getHexOneForVariant(variant);
	const colorHex2 = getVariantStringAttribute(variant, "hex-color-patch-2");

	const rawPrice = getVariantPrice(variant, currency, country);

	const price = formatMoney(rawPrice);

	const sku = getLocalSku(variant);
	const sort_order = Number.parseInt(
		variant.storyblok_variant_pages?.[0]?.sort_order ?? "0",
	);

	return variant.id && slug
		? {
				id: variant.id,
				variantUrl: `/${slug}`,
				color: getAttributeStringValue(colorAttr),
				fabric: getAttributeStringValue(fabricAttr),
				material: getAttributeStringValue(materialsAttr),
				sort_order,
				images,
				price,
				rawPrice,
				sku,
				colorHex1,
				colorHex2,
			}
		: undefined;
};
